import React from "react";
import SummaryCard from "./SummaryCard";
import { getComplianceNameById, getDrainfieldProductMakeName, getDrainfieldProductModelName } from "./summaryHelpers";
import { DrainFieldInstallationInspectionInput as AnyTypeFix } from "../DrainFieldInstallationInspectionForm.types";
import { DrainFieldInstallationInspectionInput, ComplianceData, DrainfieldProductMakeData, DrainfieldProductModelData } from "../DrainFieldInstallationInspectionForm.types";

type Props = {
    data?: DrainFieldInstallationInspectionInput;
    complianceData?: ComplianceData[];
    drainfieldProductMakeData?: DrainfieldProductMakeData[];
    drainfieldProductModelData?: DrainfieldProductModelData[];
};

const DrainfieldInstallationReadOnly: React.FC<Props> = ({ data, complianceData, drainfieldProductMakeData, drainfieldProductModelData }) => {
    if (!data) return null;

    // Helper function to check if Drainfield 2 has meaningful data
    const hasDrainfield2Data = (data: any) => {
        return (
            // Check for actual numeric values greater than 0
            (data?.drainfield2Area && data.drainfield2Area > 0) ||
            (data?.drainfield2NumberOfDrainline && data.drainfield2NumberOfDrainline > 0) ||
            (data?.drainfield2NumberOfUnits && data.drainfield2NumberOfUnits > 0) ||
            (data?.drainfield2Elevation && data.drainfield2Elevation > 0) ||
            (data?.drainfield2DosingPump && data.drainfield2DosingPump > 0) ||
            // Check for meaningful text values
            (data?.drainfield2DistributionBoxOrHeader && data.drainfield2DistributionBoxOrHeader.trim() !== '') ||
            (data?.drainfield2ElevationBenchmark && data.drainfield2ElevationBenchmark.trim() !== '') ||
            (data?.drainfield2DrainlineSeparationBedOrTrench && data.drainfield2DrainlineSeparationBedOrTrench.trim() !== '') ||
            // Check for product selections (greater than 0, not just truthy)
            (data?.drainfield2DrainfieldProductMakeId && data.drainfield2DrainfieldProductMakeId > 0) ||
            (data?.drainfield2DrainfieldProductModelId && data.drainfield2DrainfieldProductModelId > 0) ||
            // Check for compliance IDs that aren't just default values
            (data?.drainfield2AreaComplianceId && data.drainfield2AreaComplianceId > 0) ||
            (data?.drainfield2DistributionComplianceId && data.drainfield2DistributionComplianceId > 0) ||
            (data?.drainfield2NumberOfDrainlinesComplianceId && data.drainfield2NumberOfDrainlinesComplianceId > 0) ||
            (data?.drainfield2DrainlineSeparationComplianceId && data.drainfield2DrainlineSeparationComplianceId > 0) ||
            (data?.drainfield2DrainlineSlopeComplianceId && data.drainfield2DrainlineSlopeComplianceId > 0) ||
            (data?.drainfield2DepthOfCoverComplianceId && data.drainfield2DepthOfCoverComplianceId > 0) ||
            (data?.drainfield2ElevationComplianceId && data.drainfield2ElevationComplianceId > 0) ||
            (data?.drainfield2SystemLocationComplianceId && data.drainfield2SystemLocationComplianceId > 0) ||
            (data?.drainfield2DosingPumpComplianceId && data.drainfield2DosingPumpComplianceId > 0) ||
            (data?.drainfield2AggregateSizeComplianceId && data.drainfield2AggregateSizeComplianceId > 0) ||
            (data?.drainfield2AggregateExcessiveComplianceId && data.drainfield2AggregateExcessiveComplianceId > 0) ||
            (data?.drainfield2AggregateDepthComplianceId && data.drainfield2AggregateDepthComplianceId > 0) ||
            (data?.drainfield2NumberOfUnitsComplianceId && data.drainfield2NumberOfUnitsComplianceId > 0) ||
            (data?.drainfield2DrainfieldTypeComplianceId && data.drainfield2DrainfieldTypeComplianceId > 0) ||
            // Check for comments
            (data?.drainfield2AreaComment && data.drainfield2AreaComment.trim() !== '') ||
            (data?.drainfield2DistributionComment && data.drainfield2DistributionComment.trim() !== '') ||
            (data?.drainfield2NumberOfDrainlinesComment && data.drainfield2NumberOfDrainlinesComment.trim() !== '') ||
            (data?.drainfield2DrainlineSeparationComment && data.drainfield2DrainlineSeparationComment.trim() !== '') ||
            (data?.drainfield2DrainlineSlopeComment && data.drainfield2DrainlineSlopeComment.trim() !== '') ||
            (data?.drainfield2DepthOfCoverComment && data.drainfield2DepthOfCoverComment.trim() !== '') ||
            (data?.drainfield2ElevationComment && data.drainfield2ElevationComment.trim() !== '') ||
            (data?.drainfield2SystemLocationComment && data.drainfield2SystemLocationComment.trim() !== '') ||
            (data?.drainfield2DosingPumpComment && data.drainfield2DosingPumpComment.trim() !== '') ||
            (data?.drainfield2AggregateSizeComment && data.drainfield2AggregateSizeComment.trim() !== '') ||
            (data?.drainfield2AggregateExcessiveComment && data.drainfield2AggregateExcessiveComment.trim() !== '') ||
            (data?.drainfield2AggregateDepthComment && data.drainfield2AggregateDepthComment.trim() !== '') ||
            (data?.drainfield2NumberOfUnitsComment && data.drainfield2NumberOfUnitsComment.trim() !== '') ||
            (data?.drainfield2DrainfieldTypeComment && data.drainfield2DrainfieldTypeComment.trim() !== '')
        );
    };
    const renderProduct = (makeId?: any, modelId?: any) => {
        const parts: string[] = [];
        if (makeId) parts.push(`Make = ${getDrainfieldProductMakeName(drainfieldProductMakeData as any, makeId)}`);
        if (modelId) parts.push(`Model = ${getDrainfieldProductModelName(drainfieldProductModelData as any, modelId)}`);
        return parts.length ? ` (${parts.join(" ")})` : '';
    };

    const Section = ({ title, children }: any) => (
        <div className="mb-3">
            <div className="row mb-1"><div className="col col-md-12 fw-bold">{title}</div></div>
            {children}
        </div>
    );

    return (
        <>
            <SummaryCard
                title={
                    <>
                        <svg className="me-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 22l20-10L2 2v7l15 3-15 3z"/></svg>
                        Drainfield Installation
                    </>
                }
            >
                {/* Order mirrors form: Product, Area, Distribution, Number of Drainlines + Units, Separation, Slope, Depth of Cover, Elevation, Location, Dosing Pump, Aggregate Size, Excessive, Depth */}
                {/* Product */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfieldTypeComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Drainfield Type</label>
                        <div className="mt-1">
                            <span className="print-value fw-bold">{renderProduct((data as any)?.drainfieldProductMakeId, (data as any)?.drainfieldProductModelId)}</span>
                        </div>
                    </div>
                </div>
                {(data as any)?.drainfieldTypeCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.drainfieldTypeComment}</label>
                    </div>
                ) : null}

                {/* Area */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.areaComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Drainfield Area</label>
                        <div className="mt-1">
                            {(data as any)?.area1 && (
                                <span className="print-value fw-bold">{`${(data as any)?.area1} sq.ft.`}</span>
                            )}
                            {(data as any)?.area1 && (data as any)?.area2 && <span className="print-value fw-bold">, </span>}
                            {(data as any)?.area2 && (
                                <span className="print-value fw-bold">{`${(data as any)?.area2} sq.ft.`}</span>
                            )}
                        </div>
                    </div>
                </div>
                {(data as any)?.areaCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.areaComment}</label>
                    </div>
                ) : null}

                {/* Distribution */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.distributionComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Distribution Box</label>
                        <div className="mt-1">
                            {((data as any)?.distributionBoxOrHeader == "1" || (data as any)?.distributionBoxOrHeader == 1 || 
                              (typeof (data as any)?.distributionBoxOrHeader === "string" && ((data as any)?.distributionBoxOrHeader.toLowerCase() === "d-box" || (data as any)?.distributionBoxOrHeader.toLowerCase() === "box"))) && (
                                <span className="print-value fw-bold">Box</span>
                            )}
                            {((data as any)?.distributionBoxOrHeader == "2" || (data as any)?.distributionBoxOrHeader == 2 || 
                              (typeof (data as any)?.distributionBoxOrHeader === "string" && (data as any)?.distributionBoxOrHeader.toLowerCase() === "header")) && (
                                <span className="print-value fw-bold">Header</span>
                            )}
                        </div>
                    </div>
                </div>
                {(data as any)?.distributionCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.distributionComment}</label>
                    </div>
                ) : null}

                {/* Number of Drainlines + Units */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.numberOfDrainlinesComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <div className="row">
                            <div className="col col-md-6">
                                <label>Number of Drainlines</label>
                                <div className="mt-1">
                                    {(data as any)?.numberOfDrainline1 && Number((data as any)?.numberOfDrainline1) > 0 && (
                                        <span className="print-value fw-bold">{(data as any)?.numberOfDrainline1}</span>
                                    )}
                                    {(data as any)?.numberOfDrainline1 && Number((data as any)?.numberOfDrainline1) > 0 && (data as any)?.numberOfDrainline2 && Number((data as any)?.numberOfDrainline2) > 0 && <span className="print-value fw-bold">, </span>}
                                    {(data as any)?.numberOfDrainline2 && Number((data as any)?.numberOfDrainline2) > 0 && (
                                        <span className="print-value fw-bold">{(data as any)?.numberOfDrainline2}</span>
                                    )}
                                </div>
                            </div>
                            <div className="col col-md-6">
                                <label>Number of Units</label>
                                {(data as any)?.numberOfUnits && Number((data as any)?.numberOfUnits) > 0 && (
                                    <div className="mt-1">
                                        <span className="print-value fw-bold">{(data as any)?.numberOfUnits}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {(data as any)?.numberOfDrainlinesCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.numberOfDrainlinesComment}</label>
                    </div>
                ) : null}

                {/* Separation */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.drainlineSeparationComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Drain Line Separation</label>
                        <div className="mt-1">
                            {((data as any)?.drainlineSeparationBedOrTrench == "1" || (data as any)?.drainlineSeparationBedOrTrench == 1 || 
                              (typeof (data as any)?.drainlineSeparationBedOrTrench === "string" && (data as any)?.drainlineSeparationBedOrTrench.toLowerCase() === "bed")) && (
                                <span className="print-value fw-bold">Bed</span>
                            )}
                            {((data as any)?.drainlineSeparationBedOrTrench == "2" || (data as any)?.drainlineSeparationBedOrTrench == 2 || 
                              (typeof (data as any)?.drainlineSeparationBedOrTrench === "string" && (data as any)?.drainlineSeparationBedOrTrench.toLowerCase() === "trench")) && (
                                <span className="print-value fw-bold">Trench</span>
                            )}
                        </div>
                    </div>
                </div>
                {(data as any)?.drainlineSeparationCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.drainlineSeparationComment}</label>
                    </div>
                ) : null}

                {/* Slope */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.drainlineSlopeComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Drainline Slope</label>
                    </div>
                </div>
                {(data as any)?.drainlineSlopeCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.drainlineSlopeComment}</label>
                    </div>
                ) : null}

                {/* Depth of Cover */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.depthOfCoverComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Depth of Cover</label>
                    </div>
                </div>
                {(data as any)?.depthOfCoverCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.depthOfCoverComment}</label>
                    </div>
                ) : null}

                {/* Elevation */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.elevationComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Elevation</label>
                        {(data as any)?.elevation1 && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{(data as any)?.elevation1} inches {(data as any)?.elevationBenchmark === "1" ? "above" : "below"} benchmark</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.elevationCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.elevationComment}</label>
                    </div>
                ) : null}

                {/* Location */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.systemLocationComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Drainfield Location</label>
                    </div>
                </div>
                {(data as any)?.systemLocationCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.systemLocationComment}</label>
                    </div>
                ) : null}

                {/* Dosing Pump */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.dosingPumpComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Dosing Pumps</label>
                        {(data as any)?.dosingPump != 0 && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{(data as any)?.dosingPump}</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.dosingPumpCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.dosingPumpComment}</label>
                    </div>
                ) : null}

                {/* Aggregate Sizes */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.aggregateSizeComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Mineral Aggregate Size</label>
                    </div>
                </div>
                {(data as any)?.aggregateSizeCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.aggregateSizeComment}</label>
                    </div>
                ) : null}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.aggregateExcessiveComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Mineral Aggregate Excessive Fines</label>
                    </div>
                </div>
                {(data as any)?.aggregateExcessiveCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.aggregateExcessiveComment}</label>
                    </div>
                ) : null}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.aggregateDepthComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Mineral Aggregate Depth</label>
                    </div>
                </div>
                {(data as any)?.aggregateDepthCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.aggregateDepthComment}</label>
                    </div>
                ) : null}
            </SummaryCard>

            {/* Drainfield 2 */}
            {((data as any)?.isDrainfield2Showable && hasDrainfield2Data(data as any)) && (
                <SummaryCard
                    title={
                        <>
                            <svg className="me-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 22l20-10L2 2v7l15 3-15 3z"/></svg>
                            Drainfield 2 Installation
                        </>
                    }
                >
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2DrainfieldTypeComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Drainfield 2 Type</label>
                            <div className="mt-1">
                                <span className="print-value fw-bold">{renderProduct((data as any)?.drainfield2DrainfieldProductMakeId, (data as any)?.drainfield2DrainfieldProductModelId)}</span>
                            </div>
                        </div>
                    </div>
                    {(data as any)?.drainfield2DrainfieldTypeCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2DrainfieldTypeComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2AreaComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Drainfield 2 Area</label>
                                                         {(data as any)?.drainfield2Area && (
                                 <div className="mt-1">
                                     <span className="print-value fw-bold">{`${(data as any)?.drainfield2Area} sq.ft.`}</span>
                                 </div>
                             )}
                        </div>
                    </div>
                    {(data as any)?.drainfield2AreaCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2AreaComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2DistributionComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Drainfield 2 Distribution</label>
                            <div className="mt-1">
                                {((data as any)?.drainfield2DistributionBoxOrHeader == "1" || (data as any)?.drainfield2DistributionBoxOrHeader == 1 || 
                                  (typeof (data as any)?.drainfield2DistributionBoxOrHeader === "string" && ((data as any)?.drainfield2DistributionBoxOrHeader.toLowerCase() === "d-box" || (data as any)?.drainfield2DistributionBoxOrHeader.toLowerCase() === "box"))) && (
                                    <span className="print-value fw-bold">Box</span>
                                )}
                                {((data as any)?.drainfield2DistributionBoxOrHeader == "2" || (data as any)?.drainfield2DistributionBoxOrHeader == 2 || 
                                  (typeof (data as any)?.drainfield2DistributionBoxOrHeader === "string" && (data as any)?.drainfield2DistributionBoxOrHeader.toLowerCase() === "header")) && (
                                    <span className="print-value fw-bold">Header</span>
                                )}
                            </div>
                        </div>
                    </div>
                    {(data as any)?.drainfield2DistributionCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2DistributionComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2NumberOfDrainlinesComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <div className="row">
                                <div className="col col-md-6">
                                    <label>Number of Drainlines</label>
                                    {(data as any)?.drainfield2NumberOfDrainline && (
                                        <div className="mt-1">
                                            <span className="print-value fw-bold">{(data as any)?.drainfield2NumberOfDrainline}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="col col-md-6">
                                    <label>Number of Units</label>
                                    {(data as any)?.drainfield2NumberOfUnits && (
                                        <div className="mt-1">
                                            <span className="print-value fw-bold">{(data as any)?.drainfield2NumberOfUnits}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {(data as any)?.drainfield2NumberOfDrainlinesCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2NumberOfDrainlinesComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2DrainlineSeparationComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Drain Line Separation</label>
                            <div className="mt-1">
                                {((data as any)?.drainfield2DrainlineSeparationBedOrTrench == "1" || (data as any)?.drainfield2DrainlineSeparationBedOrTrench == 1 || 
                                  (typeof (data as any)?.drainfield2DrainlineSeparationBedOrTrench === "string" && (data as any)?.drainfield2DrainlineSeparationBedOrTrench.toLowerCase() === "bed")) && (
                                    <span className="print-value fw-bold">Bed</span>
                                )}
                                {((data as any)?.drainfield2DrainlineSeparationBedOrTrench == "2" || (data as any)?.drainfield2DrainlineSeparationBedOrTrench == 2 || 
                                  (typeof (data as any)?.drainfield2DrainlineSeparationBedOrTrench === "string" && (data as any)?.drainfield2DrainlineSeparationBedOrTrench.toLowerCase() === "trench")) && (
                                    <span className="print-value fw-bold">Trench</span>
                                )}
                            </div>
                        </div>
                    </div>
                    {(data as any)?.drainfield2DrainlineSeparationCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2DrainlineSeparationComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2DrainlineSlopeComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Drainline Slope</label>
                        </div>
                    </div>
                    {(data as any)?.drainfield2DrainlineSlopeCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2DrainlineSlopeComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2DepthOfCoverComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Depth of Cover</label>
                        </div>
                    </div>
                    {(data as any)?.drainfield2DepthOfCoverCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2DepthOfCoverComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2ElevationComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Elevation</label>
                            {(data as any)?.drainfield2Elevation && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{(data as any)?.drainfield2Elevation} inches {(data as any)?.drainfield2ElevationBenchmark === "1" ? "above" : "below"} benchmark</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.drainfield2ElevationCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2ElevationComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2SystemLocationComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Drainfield 2 Location</label>
                        </div>
                    </div>
                    {(data as any)?.drainfield2SystemLocationCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2SystemLocationComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2DosingPumpComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Dosing Pumps</label>
                            {(data as any)?.drainfield2DosingPump != 0 && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{(data as any)?.drainfield2DosingPump}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.drainfield2DosingPumpCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2DosingPumpComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2AggregateSizeComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Mineral Aggregate Size</label>
                        </div>
                    </div>
                    {(data as any)?.drainfield2AggregateSizeCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2AggregateSizeComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2AggregateExcessiveComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Mineral Aggregate Excessive Fines</label>
                        </div>
                    </div>
                    {(data as any)?.drainfield2AggregateExcessiveCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2AggregateExcessiveComment}</label>
                        </div>
                    ) : null}

                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.drainfield2AggregateDepthComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Mineral Aggregate Depth</label>
                        </div>
                    </div>
                    {(data as any)?.drainfield2AggregateDepthCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.drainfield2AggregateDepthComment}</label>
                        </div>
                    ) : null}


                </SummaryCard>
            )}
        </>
    );
};

export default DrainfieldInstallationReadOnly;


